import React from "react";
import { Helmet } from "react-helmet-async";

export default function DtfGhosting() {
  return (
    <>
      <Helmet>
        <title>DTF Ghosting? Why You’re Getting a Double Image</title>
        <meta
          name="description"
          content="DTF ghosting or double image issues? Learn the most common causes of shadowing, offset prints, and duplicate image problems so you can fix the real source."
        />
      </Helmet>

      <div className="px-4 py-10 max-w-3xl mx-auto text-white">
        <h1 className="text-3xl font-bold mb-6 text-center">
          DTF Ghosting? Here’s Why Your Print Looks Shadowed or Doubled
        </h1>

        <p className="mb-6 text-lg text-gray-300">
          If your DTF print looks shadowed, doubled, or slightly offset, you
          are likely dealing with ghosting. This can make artwork look blurry,
          messy, or out of register even when the design itself is clean.
          Ghosting is especially frustrating because it can start during
          printing, or happen later during transfer and handling.
        </p>

        <p className="mb-6 text-gray-300">
          That overlap is what makes this issue hard to diagnose. Some cases are
          caused by printer alignment, while others come from film movement or
          shifting during the press process.
        </p>

        <div className="mb-8 p-5 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            Ghosting Can Start in Printing or During Transfer
          </h2>
          <p className="text-gray-300 mb-4">
            DTF Wizard helps break down whether your ghosting issue is coming
            from alignment, film movement, transfer handling, or output setup so
            you can stop guessing and fix the right part of the process.
          </p>
          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Common Causes of DTF Ghosting
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>Bi-directional alignment issues</li>
          <li>Film movement during printing or pressing</li>
          <li>Printhead or carriage movement inconsistencies</li>
          <li>Improper transfer handling during the press process</li>
          <li>Artwork or RIP output not aligning properly</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          What DTF Ghosting Usually Looks Like
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>Shadowed edges around the design</li>
          <li>A slight duplicate image or offset look</li>
          <li>Artwork appearing out of register</li>
          <li>Problem is consistent in one direction</li>
          <li>Print looked fine in file form but wrong in output</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Quick Things to Check First
        </h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-300">
          <li>See whether the shadowing starts during print or after pressing</li>
          <li>Check whether the offset is consistent in one direction</li>
          <li>Watch for any film shifting or unwanted movement</li>
          <li>Compare whether the problem appears on all designs or only some files</li>
          <li>Look at whether the issue is tied to printer movement or press handling</li>
        </ul>

        <h2 className="text-2xl font-semibold mb-3 mt-8">
          Why This Is Hard to Fix
        </h2>
        <p className="text-gray-300">
          Ghosting can come from more than one stage of the process. Sometimes
          it starts in printing, other times it happens during transfer or
          handling. Because the same shadowed result can come from different
          causes, it is easy to blame the wrong thing and keep chasing it.
        </p>

        <div className="mt-10 p-6 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            Want the Exact Fix for Your Setup?
          </h2>

          <p className="text-gray-300 mb-4">
            DTF Wizard helps identify whether your ghosting issue is coming from
            printer alignment, film movement, transfer handling, or output setup
            so you can fix the actual cause instead of guessing.
          </p>

          <p className="text-gray-400 mb-4 italic">
            Fix one issue and this already pays for itself.
          </p>

          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-semibold mb-4">FAQ</h2>

          <div className="space-y-4 text-gray-300">
            <p>
              <strong>Why does my DTF print look like it has a shadow?</strong>
              <br />
              That usually means part of the image is being duplicated, shifted,
              or printed out of alignment somewhere in the process.
            </p>

            <p>
              <strong>Can pressing cause ghosting?</strong>
              <br />
              Yes. If the transfer shifts during handling or the press process,
              it can create a shadowed or doubled look even if the print was
              clean beforehand.
            </p>

            <p>
              <strong>Is ghosting the same as blurry printing?</strong>
              <br />
              Not always. Blurry prints usually look soft overall, while
              ghosting typically shows a second image, shadow, or offset.
            </p>
          </div>
        </div>

        <div className="mt-12 text-gray-400">
          <p className="mb-2 font-semibold">Related DTF Issues:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <a
                href="/fix/dtf-banding-lines"
                className="text-blue-400 hover:underline"
              >
                DTF Banding Lines
              </a>
            </li>
            <li>
              <a
                href="/fix/dtf-ink-smearing"
                className="text-blue-400 hover:underline"
              >
                DTF Ink Smearing
              </a>
            </li>
            <li>
              <a
                href="/fix/dtf-underbase-misalignment"
                className="text-blue-400 hover:underline"
              >
                DTF Underbase Misalignment
              </a>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}